import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  X, CheckCircle2, Plus, Trash2, Edit3, Save, Check
} from 'lucide-react'
import { normalizeAnswerIndex } from '../../data/moduleSchema'

export default function ModuleContentEditorModal({ isOpen, onClose, schema, onSave }) {
  const [editedSchema, setEditedSchema] = useState(() => JSON.parse(JSON.stringify(schema || {})))
  const [activePageIndex, setActivePageIndex] = useState(0)

  useEffect(() => {
    if (schema) {
      setEditedSchema(JSON.parse(JSON.stringify(schema)))
    }
  }, [schema, isOpen])

  if (!isOpen || !editedSchema) return null

  const pages = editedSchema.pages || []
  const currentPage = pages[activePageIndex] || {}

  const handleUpdatePageField = (field, value) => {
    const nextPages = [...pages]
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      [field]: value,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleUpdateCallout = (field, value) => {
    const nextPages = [...pages]
    const currentCallout = nextPages[activePageIndex]?.callout || { type: 'tip', text: '' }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      callout: {
        ...currentCallout,
        [field]: value,
      },
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleAddQuestion = () => {
    const nextPages = [...pages]
    const questions = nextPages[activePageIndex]?.questions || []
    const newQ = {
      question: 'New quick-check question',
      options: ['Correct Option', 'Distractor Option 1', 'Distractor Option 2'],
      answer: 0,
      correctIndex: 0,
      explanation: 'Explanation explaining why this option is correct.',
    }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions: [...questions, newQ],
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleDeleteQuestion = (qIdx) => {
    const nextPages = [...pages]
    const questions = (nextPages[activePageIndex]?.questions || []).filter((_, i) => i !== qIdx)
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleUpdateQuestion = (qIdx, field, value) => {
    const nextPages = [...pages]
    const questions = [...(nextPages[activePageIndex]?.questions || [])]
    questions[qIdx] = {
      ...questions[qIdx],
      [field]: value,
    }
    // If updating answer, keep correctIndex in sync
    if (field === 'answer') {
      questions[qIdx].correctIndex = value
    }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleUpdateOption = (qIdx, optIdx, text) => {
    const nextPages = [...pages]
    const questions = [...(nextPages[activePageIndex]?.questions || [])]
    const options = [...(questions[qIdx]?.options || [])]
    options[optIdx] = text
    questions[qIdx] = {
      ...questions[qIdx],
      options,
    }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleAddOption = (qIdx) => {
    const nextPages = [...pages]
    const questions = [...(nextPages[activePageIndex]?.questions || [])]
    const options = [...(questions[qIdx]?.options || []), `Option ${(questions[qIdx]?.options?.length || 0) + 1}`]
    questions[qIdx] = {
      ...questions[qIdx],
      options,
    }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleDeleteOption = (qIdx, optIdx) => {
    const nextPages = [...pages]
    const questions = [...(nextPages[activePageIndex]?.questions || [])]
    const options = (questions[qIdx]?.options || []).filter((_, i) => i !== optIdx)
    if (options.length < 2) return // keep minimum 2 options

    let currentAns = questions[qIdx].answer ?? 0
    if (currentAns >= options.length) {
      currentAns = 0
    }
    questions[qIdx] = {
      ...questions[qIdx],
      options,
      answer: currentAns,
      correctIndex: currentAns,
    }
    nextPages[activePageIndex] = {
      ...nextPages[activePageIndex],
      questions,
    }
    setEditedSchema({ ...editedSchema, pages: nextPages })
  }

  const handleSaveAndApply = () => {
    onSave(editedSchema)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-xs">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
              <Edit3 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Edit Module Content & Quiz</h2>
              <p className="text-xs text-slate-500">
                Inspect and modify section content, quiz options, and correct answers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAndApply}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs transition-colors"
            >
              <Save className="h-3.5 w-3.5" />
              Save Changes
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="px-6 py-2 border-b border-slate-100 bg-white flex items-center gap-2 overflow-x-auto">
          {pages.map((p, i) => (
            <button
              key={i}
              onClick={() => setActivePageIndex(i)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
                activePageIndex === i
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-2xs'
                  : 'text-slate-600 hover:bg-slate-50 border border-transparent'
              }`}
            >
              <span>Section {i + 1}: {p.title || `Page ${i + 1}`}</span>
              {(p.questions?.length || 0) > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-indigo-100 text-indigo-800 text-[10px]">
                  {p.questions.length} {p.questions.length === 1 ? 'quiz' : 'quizzes'}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body Content & Questions Editor */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Section Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Section Title
              </label>
              <input
                type="text"
                value={currentPage.title || ''}
                onChange={(e) => handleUpdatePageField('title', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Section Subtitle
              </label>
              <input
                type="text"
                value={currentPage.subtitle || ''}
                onChange={(e) => handleUpdatePageField('subtitle', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Section Markdown Content */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Section Content (Markdown formatted)
            </label>
            <textarea
              rows={6}
              value={currentPage.content || ''}
              onChange={(e) => handleUpdatePageField('content', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
            />
          </div>

          {/* Callout Box */}
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Callout Box (Optional)
              </span>
              <select
                value={currentPage.callout?.type || 'tip'}
                onChange={(e) => handleUpdateCallout('type', e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs text-slate-700 focus:outline-none"
              >
                <option value="tip">Pro Tip (Green)</option>
                <option value="key">Key Takeaway (Indigo)</option>
                <option value="warning">Watch Out (Amber)</option>
                <option value="info">Info Note (Blue)</option>
              </select>
            </div>
            <input
              type="text"
              value={currentPage.callout?.text || ''}
              onChange={(e) => handleUpdateCallout('text', e.target.value)}
              placeholder="Callout body text..."
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          {/* Questions & Quiz Editor for This Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Quick Check Questions for Section {activePageIndex + 1}
                </h3>
                <p className="text-xs text-slate-500">
                  Trainees must solve these questions at the bottom of this section.
                </p>
              </div>
              <button
                onClick={handleAddQuestion}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Question
              </button>
            </div>

            {(!currentPage.questions || currentPage.questions.length === 0) ? (
              <div className="p-6 border border-dashed border-slate-300 rounded-2xl text-center">
                <p className="text-xs text-slate-500 mb-2">No check questions on this section yet.</p>
                <button
                  onClick={handleAddQuestion}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-700 text-xs font-semibold"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Quick Check
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {currentPage.questions.map((q, qIdx) => {
                  const currentAnswerIdx = normalizeAnswerIndex(q, q.options || [])

                  return (
                    <div
                      key={qIdx}
                      className="p-5 bg-white border border-slate-200 rounded-2xl shadow-xs space-y-4"
                    >
                      {/* Question Header & Delete */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-bold">
                              QUESTION {qIdx + 1}
                            </span>
                            <span className="text-[11px] text-slate-400">
                              (Click the button on the correct answer below)
                            </span>
                          </div>
                          <input
                            type="text"
                            value={q.question || ''}
                            onChange={(e) => handleUpdateQuestion(qIdx, 'question', e.target.value)}
                            placeholder="Question prompt..."
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                          />
                        </div>
                        <button
                          onClick={() => handleDeleteQuestion(qIdx)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete question"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Options List */}
                      <div className="space-y-2">
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Options & Correct Answer
                        </label>
                        {(q.options || []).map((opt, optIdx) => {
                          const isCorrect = optIdx === currentAnswerIdx

                          return (
                            <div
                              key={optIdx}
                              className={`flex items-center gap-2.5 p-2 rounded-xl border transition-all ${
                                isCorrect
                                  ? 'bg-emerald-50/60 border-emerald-300 ring-1 ring-emerald-300'
                                  : 'bg-white border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              {/* Button to mark correct answer */}
                              <button
                                type="button"
                                onClick={() => handleUpdateQuestion(qIdx, 'answer', optIdx)}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all shrink-0 ${
                                  isCorrect
                                    ? 'bg-emerald-600 text-white shadow-2xs'
                                    : 'bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-800'
                                }`}
                                title="Click to mark this option as the correct answer"
                              >
                                {isCorrect ? (
                                  <>
                                    <Check className="h-3 w-3" />
                                    <span>Correct Answer</span>
                                  </>
                                ) : (
                                  <span>Mark Correct</span>
                                )}
                              </button>

                              <span className="text-xs font-mono font-bold text-slate-400 w-4">
                                {String.fromCharCode(65 + optIdx)}.
                              </span>

                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => handleUpdateOption(qIdx, optIdx, e.target.value)}
                                className="flex-1 bg-transparent border-none text-xs text-slate-800 focus:outline-none"
                              />

                              {(q.options?.length || 0) > 2 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteOption(qIdx, optIdx)}
                                  className="p-1 rounded-md text-slate-300 hover:text-red-500 transition-colors"
                                  title="Delete option"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          )
                        })}

                        <button
                          type="button"
                          onClick={() => handleAddOption(qIdx)}
                          className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1 mt-1"
                        >
                          <Plus className="h-3 w-3" />
                          Add Option
                        </button>
                      </div>

                      {/* Explanation */}
                      <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                          Explanation (shown after answer is submitted)
                        </label>
                        <input
                          type="text"
                          value={q.explanation || ''}
                          onChange={(e) => handleUpdateQuestion(qIdx, 'explanation', e.target.value)}
                          placeholder="Why is the chosen option correct?"
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/70">
          <span className="text-xs text-slate-500">
            Changes apply immediately to your live interactive preview canvas.
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndApply}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Apply & Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
